import React, { useState } from 'react'
import { bodyCss } from '../../../legacy-design-system/text'
import { css, cx } from '@emotion/css'
import { Cve } from '../shared'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { VulnerabilityModal } from '../../vulnerability-modal'
import {
  AccessComplexity,
  AccessVector,
  AuthenticationRequired,
  AvailabilityImpact,
  ConfidentialityImpact,
  IntegrityImpact,
  Vulnerability
} from '../../shared'
import {
  VulnerabilitySeverity,
  VulnerabilitySeverityRegistry
} from '../../../graphql/schema/shared'
import { OrganizationFlagBetaBadge } from '../../badge/organization-flag-beta-badge'
import { WithTooltip } from '../../../design-system'
import { useIntl } from 'react-intl'
import { Link } from '@contrast-security-inc/design-system-typography'

export const nameDataTestID = 'cve-shield-cell-name'
export const cvesDataTestID = 'cve-shield-cell-cves'
export const betaContainerDataTestID = 'cve-shield-cell-beta'

export interface CveShieldCellProps {
  readonly rowData: {
    readonly name: string
    readonly cves: Cve[] | null
  }
}

export function CveShieldCell({
  rowData: { name: cveShieldName, cves }
}: CveShieldCellProps) {
  const [showCVEDetailDialog, setShowCVEDetailDialog] = useState<boolean>(false)
  const [selectedCVE, setSelectedCVE] = useState<Cve | null>()

  const { formatMessage } = useIntl()

  return (
    <>
      <div
        data-testid={nameDataTestID}
        className={cx(
          bodyCss,
          css`
            color: #000000;
          `
        )}
      >
        {cveShieldName}
      </div>

      {cves && (
        <div data-testid={cvesDataTestID}>
          {cves.map((cve, index) => {
            const { name, description } = cve
            const cveName = name.toUpperCase()

            return (
              <div key={index}>
                {description ? (
                  <Link
                    className={cx(
                      css`
                        font-size: 12px;
                      `
                    )}
                    onClick={() => {
                      setSelectedCVE(cve)
                      setShowCVEDetailDialog(true)
                    }}
                  >
                    {cveName}
                  </Link>
                ) : (
                  <span
                    className={css`
                      font-size: 12px;
                      color: ${theme.color.neutral70};
                    `}
                  >
                    {cveName}
                  </span>
                )}
                <div
                  data-testid={betaContainerDataTestID}
                  className={css`
                    display: inline-block;
                    margin: 0 5px;
                    vertical-align: 1px;
                  `}
                >
                  {isBetaCveShield(cveName) && (
                    <WithTooltip
                      renderTooltipContent={() =>
                        formatMessage({
                          id: 'detail_labelBeta_betaRuleDescription'
                        })
                      }
                      placement="right"
                    >
                      {() => <OrganizationFlagBetaBadge />}
                    </WithTooltip>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {selectedCVE && (
        <VulnerabilityModal
          vulnerability={mapCveToVulnerability(selectedCVE)}
          isOpen={showCVEDetailDialog}
          isCVEModalOpen={false}
          onCancel={() => {
            setSelectedCVE(null)
            setShowCVEDetailDialog(false)
          }}
        />
      )}
    </>
  )
}

export function mapCveToVulnerability({
  name,
  description,
  authentication,
  references,
  accessVector,
  accessComplexity,
  availabilityImpact,
  confidentialityImpact,
  integrityImpact,
  score
}: Cve): Vulnerability {
  return {
    name,
    description: description as string,
    authentication: authentication as AuthenticationRequired,
    references,
    accessVector: accessVector as AccessVector,
    accessComplexity: accessComplexity as AccessComplexity,
    availabilityImpact: availabilityImpact as AvailabilityImpact,
    confidentialityImpact: confidentialityImpact as ConfidentialityImpact,
    integrityImpact: integrityImpact as IntegrityImpact,
    severityValue: score,
    severityCode: calculateAppSeverityCode(score),
    visible: true,
    hasCve: true
  }
}

export function calculateAppSeverityCode(score: number): VulnerabilitySeverity {
  if (score >= 7) {
    return VulnerabilitySeverityRegistry.HIGH
  } else if (score >= 4) {
    return VulnerabilitySeverityRegistry.MEDIUM
  }
  return VulnerabilitySeverityRegistry.LOW
}

export function isBetaCveShield(cveName: string): boolean {
  const betaCveShields: string[] = [
    'cve-2017-0898',
    'cve-2017-12616',
    'cve-2017-12617',
    'cve-2017-14033',
    'cve-2017-14064',
    'cve-2021-44228',
    'cve-2021-45046'
  ]

  return betaCveShields.includes(cveName.toLocaleLowerCase())
}
